import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useLocationStore } from '@/stores/location'
import { useBusinessStore } from '@/stores/business'
import { orderAheadService } from '@/services'

const POLL_VISIBLE_MS = 8000
const POLL_HIDDEN_MS = 60000

export const useOrderAheadQueueStore = defineStore('orderAheadQueue', () => {
  const sales = ref([])
  const loading = ref(false)
  const error = ref('')

  let pollTimer = null
  let subscriberCount = 0
  let locationWatchStop = null

  function clearPoll () {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  function schedulePoll () {
    clearPoll()
    if (subscriberCount <= 0) return
    const ms = typeof document !== 'undefined' && document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS
    pollTimer = window.setInterval(() => {
      fetchQueue()
    }, ms)
  }

  function onVisibilityChange () {
    schedulePoll()
    if (typeof document !== 'undefined' && !document.hidden) {
      fetchQueue()
    }
  }

  async function fetchQueue () {
    const businessStore = useBusinessStore()
    if (!businessStore.info?.order_ahead_enabled) {
      sales.value = []
      error.value = ''
      loading.value = false
      return
    }

    const locationStore = useLocationStore()
    const locId = locationStore.getCurrentLocation?.id
    if (!locId) {
      sales.value = []
      return
    }
    loading.value = true
    error.value = ''
    try {
      const data = await orderAheadService.fetchOrderAheadQueue(locId)
      sales.value = data.sales || []
    } catch (e) {
      error.value = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Failed to load queue'
      sales.value = []
    } finally {
      loading.value = false
    }
  }

  /**
   * PATCH fulfillment only; caller may await dwell then fetchQueue().
   */
  async function patchFulfillment (saleId, fulfillmentStatus) {
    await orderAheadService.updateOrderAheadFulfillment(saleId, fulfillmentStatus)
  }

  const queueCount = computed(() => sales.value.length)

  function subscribe () {
    subscriberCount++
    if (subscriberCount === 1) {
      const locationStore = useLocationStore()
      locationWatchStop = watch(
        () => locationStore.getCurrentLocation?.id,
        () => {
          fetchQueue()
        }
      )
      fetchQueue()
      schedulePoll()
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange)
      }
    }
  }

  function unsubscribe () {
    subscriberCount = Math.max(0, subscriberCount - 1)
    if (subscriberCount === 0) {
      clearPoll()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }
      if (locationWatchStop) {
        locationWatchStop()
        locationWatchStop = null
      }
    }
  }

  return {
    sales,
    loading,
    error,
    queueCount,
    fetchQueue,
    patchFulfillment,
    subscribe,
    unsubscribe
  }
})
